'use client';

import { type JSX, type RefObject, useCallback, useRef, useState } from 'react';
import { Separator } from '@/components/ui/separator';
import { VisualHeader } from '@/components/Visualization/VisualMenu/modules/VisualHeader';
import type { ChartValues, Data } from '@/types/chart';
import VisualMenuDropdown from './modules/Dropdown';
import { VisualMenuContext } from './provider/VisualMenuContext';
import { getLatestDataPoint } from './utils/getLatestDataPoint';

type VisualMenuProps = {
  title: string;
  subtitle?: string;
  description: string;
  unit?: string;
  exportToExcel?: () => void;
  isLoading?: boolean;
  isError?: boolean;
  queryLabel?: string;
  showLatestDataPoints?: boolean;
  enableAnnotations?: boolean;
  children(args: { showAnnotations: boolean; ref: RefObject<HTMLDivElement | null> }): JSX.Element;
};

export const VisualMenu = ({
  title = 'Grafens titel',
  subtitle,
  description = 'Defintionen av Y-axeln, t.ex. "Kronor per kvadratmeter',
  unit = '',
  exportToExcel,
  isLoading = false,
  isError = false,
  queryLabel = '',
  showLatestDataPoints = true,
  enableAnnotations = true,
  children,
}: VisualMenuProps) => {
  const ref = useRef<HTMLDivElement>(null);
  const [showAnnotations, setShowAnnotations] = useState(false);
  const [latestDataPoints, setLatestDataPoints] = useState<Data[] | null>(null);

  const handleLatestDataPoints = useCallback((data: ChartValues[][] | ChartValues[]) => {
    const tempData = data.flat();
    setLatestDataPoints(getLatestDataPoint(tempData));
  }, []);

  return (
    <VisualMenuContext.Provider value={handleLatestDataPoints}>
      <div className='bg-bg rounded-lg border'>
        <VisualHeader
          title={title}
          subtitle={subtitle}
          unit={unit}
          latestDataPoints={latestDataPoints}
          showLatestDataPoints={showLatestDataPoints}
        />
        <Separator orientation='horizontal' />
        <div className='mt-3 flex items-center justify-between gap-4 px-5'>
          <p className='text-primary text-sm font-semibold text-balance first-letter:capitalize'>{description}</p>
          <div className='flex h-full translate-x-1.5 items-center gap-2 text-sm print:hidden'>
            <VisualMenuDropdown
              chartRef={ref}
              chartTitle={title}
              unit={unit}
              queryLabel={queryLabel}
              exportToExcel={exportToExcel}
              showAnnotations={showAnnotations}
              exportDisabled={isLoading || isError}
              enableAnnotations={enableAnnotations}
              handleShowAnnotations={() => setShowAnnotations(!showAnnotations)}
            />
          </div>
        </div>
        <div className='mx-5'>
          <Separator
            orientation='horizontal'
            className='bg-primary mt-3 h-0.5 w-full'
          />
        </div>
        {children({ showAnnotations, ref })}
      </div>
    </VisualMenuContext.Provider>
  );
};
